import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  buildConfirmReply,
  buildCancelReply
} from '../adapters/services/EnrichmentTagService';
import { REVEAL_TOKEN_TTL_MS } from '../../../utils/constants/agentEnrichment';
import {
  eventAgentEnrichmentRevealConfirmShown,
  eventAgentEnrichmentRevealConfirmed,
  eventAgentEnrichmentRevealCancelled
} from '../../../ga4/ga4';

/**
 * Confirm-before-spend card (TD-2293 / shipped TD-2281 contract).
 *
 * Renders from a parsed `[REVEAL_PENDING:<token>:<cost>:<remaining>]` tag:
 * total credit cost, remaining balance, and balance-after. The tag carries no
 * allowance, so the total the balance is measured against comes down as
 * `creditLimit` from the page's subscription status (TD-2884). The contact
 * itemization lives in the agent's prose in the same turn — the tag carries
 * only the three scalars. Confirm/Cancel send the hidden control replies
 * (`CONFIRM_REVEAL:<token>` / `CANCEL_REVEAL:<token>`); the agent's answer
 * (render turn or the deterministic cancel ack) arrives as a normal message.
 *
 * The token is single-use and expires ~3 minutes after the quote (backend
 * REVEAL_TOKEN_TTL_MS). Expiry is enforced server-side (403, zero spend) — the
 * client-side timer only greys a stale card to save the user a dead click.
 */

const STATE = {
  IDLE: 'idle',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
};

const RevealConfirmCard = ({
  revealPending,
  messageTimestamp = null,
  onControlReply = null,
  creditLimit = null
}) => {
  const { token, cost, remaining } = revealPending;

  // Time left on the quote, measured from the quote message's timestamp.
  // Unknown timestamp → treat as fresh (server enforces real expiry anyway).
  const expiresAtMs = useMemo(() => {
    const issued = messageTimestamp ? new Date(messageTimestamp).getTime() : Date.now();
    return issued + REVEAL_TOKEN_TTL_MS;
  }, [messageTimestamp]);

  const [state, setState] = useState(() =>
    Date.now() >= expiresAtMs ? STATE.EXPIRED : STATE.IDLE
  );

  // GA4 (TD-2293): the quote/confirm card was surfaced. Metadata only (cost).
  const shownFiredRef = useRef(false);
  useEffect(() => {
    if (!shownFiredRef.current) {
      shownFiredRef.current = true;
      eventAgentEnrichmentRevealConfirmShown(typeof cost === 'number' ? cost : 0);
    }
  }, [cost]);

  // Flip an untouched card to expired when the TTL elapses while it is open.
  useEffect(() => {
    if (state !== STATE.IDLE) return undefined;
    const msLeft = expiresAtMs - Date.now();
    if (msLeft <= 0) {
      setState(STATE.EXPIRED);
      return undefined;
    }
    const timer = setTimeout(() => {
      setState((current) => (current === STATE.IDLE ? STATE.EXPIRED : current));
    }, msLeft);
    return () => clearTimeout(timer);
  }, [state, expiresAtMs]);

  const disabled = state !== STATE.IDLE || !onControlReply;
  const balanceAfter =
    typeof remaining === 'number' && typeof cost === 'number' ? remaining - cost : null;

  // TD-2884: measure the balance against what the customer bought, not against
  // what happens to be left — "1,727 of 1,728" made a 2,500-credit contract look
  // like a 1,728-credit one, and shrank every time they spent. Without a known
  // allowance the figure stands alone rather than quoting a wrong total.
  const hasTotal =
    typeof creditLimit === 'number' &&
    creditLimit > 0 &&
    balanceAfter !== null &&
    balanceAfter <= creditLimit;
  const balanceAfterText = () => {
    if (balanceAfter === null) return '—';
    if (!hasTotal) return `${balanceAfter.toLocaleString()} credits`;
    return `${balanceAfter.toLocaleString()} of ${creditLimit.toLocaleString()} credits`;
  };

  const answer = (nextState, reply) => {
    if (disabled) return;
    setState(nextState);
    if (nextState === STATE.CONFIRMED) {
      eventAgentEnrichmentRevealConfirmed(typeof cost === 'number' ? cost : 0);
    } else if (nextState === STATE.CANCELLED) {
      eventAgentEnrichmentRevealCancelled();
    }
    onControlReply(reply);
  };

  const statusNote = {
    [STATE.CONFIRMED]: 'Confirmed — revealing…',
    [STATE.CANCELLED]: 'Cancelled — no credits were spent.',
    [STATE.EXPIRED]: 'This quote has expired — nothing was charged. Ask again for a fresh quote.'
  }[state];

  return (
    <section
      className={`reveal-confirm-card${state === STATE.EXPIRED ? ' reveal-confirm-card-expired' : ''}`}
      aria-label="Confirm reveal"
    >
      <div className="reveal-confirm-card-title">Confirm reveal</div>

      <div className="reveal-confirm-card-rows">
        <div className="reveal-confirm-card-row">
          <span className="reveal-confirm-card-label">Total cost</span>
          <span className="reveal-confirm-card-value">
            {typeof cost === 'number' ? `${cost.toLocaleString()} credits` : '—'}
          </span>
        </div>
        {typeof remaining === 'number' && (
          <div className="reveal-confirm-card-row">
            <span className="reveal-confirm-card-label">Balance after</span>
            <span className="reveal-confirm-card-value">{balanceAfterText()}</span>
          </div>
        )}
      </div>

      <div className="reveal-confirm-card-hint">Not-found contacts aren&apos;t charged.</div>

      {statusNote ? (
        <output className="reveal-confirm-card-status" aria-live="polite">
          {statusNote}
        </output>
      ) : (
        <div className="reveal-confirm-card-actions">
          <button
            type="button"
            className="reveal-confirm-card-cancel"
            disabled={disabled}
            onClick={() => answer(STATE.CANCELLED, buildCancelReply(token))}
          >
            Cancel
          </button>
          <button
            type="button"
            className="reveal-confirm-card-confirm"
            disabled={disabled}
            onClick={() => answer(STATE.CONFIRMED, buildConfirmReply(token))}
          >
            {typeof cost === 'number' ? `Confirm · ${cost.toLocaleString()} credits` : 'Confirm'}
          </button>
        </div>
      )}
    </section>
  );
};

RevealConfirmCard.propTypes = {
  /** Parsed [REVEAL_PENDING] payload from message.enrichment. */
  revealPending: PropTypes.shape({
    token: PropTypes.string.isRequired,
    cost: PropTypes.number,
    remaining: PropTypes.number
  }).isRequired,
  /** Timestamp of the quote message — drives the stale/expired treatment. */
  messageTimestamp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date)
  ]),
  /** Sends the hidden control reply (CONFIRM_REVEAL:/CANCEL_REVEAL:). */
  onControlReply: PropTypes.func,
  /**
   * The company's total contact-credit allowance (TD-2884), used as the
   * denominator for "balance after". Null when it isn't known, which drops the
   * denominator rather than falling back to the remaining balance.
   */
  creditLimit: PropTypes.number
};

export default RevealConfirmCard;
